import { useState, useMemo } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";

interface TransferDialogProps {
  open: boolean;
  currentDistrictName: string | null;
  currentRegionName: string | null;
  districts: District[];
  regions: Region[];
  onConfirm: (
    newDistrictId: string,
    newRegionId: string,
    eventDate: string,
    notes?: string
  ) => Promise<void>;
  onCancel: () => void;
}

export function TransferDialog({
  open,
  currentDistrictName,
  currentRegionName,
  districts,
  regions,
  onConfirm,
  onCancel,
}: TransferDialogProps) {
  const todayIso = new Date().toISOString().split("T")[0];

  const [newDistrictId, setNewDistrictId] = useState("");
  const [newRegionId, setNewRegionId] = useState("");
  const [eventDate, setEventDate] = useState(todayIso);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [districtError, setDistrictError] = useState(false);
  const [regionError, setRegionError] = useState(false);

  const availableRegions = useMemo(
    () => regions.filter((r) => r.district_id === newDistrictId),
    [regions, newDistrictId]
  );

  const handleDistrictChange = (districtId: string) => {
    setNewDistrictId(districtId);
    setNewRegionId("");
    setDistrictError(false);
  };

  const handleConfirm = async () => {
    let valid = true;
    if (!newDistrictId) { setDistrictError(true); valid = false; }
    if (!newRegionId) { setRegionError(true); valid = false; }
    if (!valid) return;

    setIsSubmitting(true);
    try {
      await onConfirm(newDistrictId, newRegionId, eventDate, notes.trim() || undefined);
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setNewDistrictId("");
    setNewRegionId("");
    setEventDate(todayIso);
    setNotes("");
    setDistrictError(false);
    setRegionError(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) { reset(); onCancel(); }
  };

  const resetAndCancel = () => { reset(); onCancel(); };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Traslado de Distrito / Región</AlertDialogTitle>
          <AlertDialogDescription>
            Ubicación actual:{" "}
            <span className="font-medium text-foreground">
              {currentDistrictName ?? "—"}{currentRegionName ? ` / ${currentRegionName}` : ""}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="new-district">
              Nuevo Distrito <span className="text-destructive">*</span>
            </Label>
            <Select value={newDistrictId} onValueChange={handleDistrictChange}>
              <SelectTrigger id="new-district" className={districtError ? "border-destructive" : ""}>
                <SelectValue placeholder="Seleccionar distrito..." />
              </SelectTrigger>
              <SelectContent>
                {districts.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {districtError && <p className="text-sm text-destructive">El distrito es obligatorio.</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-region">
              Nueva región <span className="text-destructive">*</span>
            </Label>
            <Select
              value={newRegionId}
              onValueChange={(v) => { setNewRegionId(v); setRegionError(false); }}
              disabled={!newDistrictId}
            >
              <SelectTrigger id="new-region" className={regionError ? "border-destructive" : ""}>
                <SelectValue placeholder={newDistrictId ? "Seleccionar región..." : "Seleccione primero un distrito"} />
              </SelectTrigger>
              <SelectContent>
                {availableRegions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {regionError && <p className="text-sm text-destructive">La región es obligatoria.</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="transfer-date">Fecha del traslado</Label>
            <Input
              id="transfer-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              max={todayIso}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="transfer-notes">Notas (opcional)</Label>
            <Textarea
              id="transfer-notes"
              placeholder="Observaciones adicionales..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetAndCancel} disabled={isSubmitting}>
            Cancelar
          </AlertDialogCancel>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            Confirmar traslado
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
